// power/model.js — turns Amber, Sigen and Solcast entities, and Amber's
// settled statistics, into what the power card and page draw. Pure
// functions, so the rules can be tested without React.
//
// Which figure comes from where (Ben, 2026-10-22: "Amber is the real price
// data"):
//   - price now: Amber's live feed (the HA integration)
//   - the forecast: Amber's own prediction, with its low-high range, from
//     ha-battery's sensor.amber_general_predicted_forecast. The integration's
//     per_kwh forecast is built from AEMO predispatch and ran far high: on
//     the night of 2026-10-26 it called 43c at 10 pm against 25.6c settled,
//     where Amber's prediction missed by 1.8c on average all evening. The
//     per_kwh list is the fallback if the prediction sensor is ever empty.
//   - past days, in dollars and grid kWh: Amber's SETTLED figures, written
//     into HA nightly as amber:* statistics by ha-battery/scripts/amber_to_ha.py.
//     They arrive the day after, so "yesterday" is the newest settled day.
//   - today so far: HA's own running estimate, labelled as one. It priced
//     the 12 Oct spike sale at $261 against Amber's $195, so it is never
//     shown for a finished day.
//   - solar, battery in and out: Sigen (Amber cannot see behind the meter).
//     Lifetime counters, not the daily_* ones: the daily import counter
//     invented 17.6 kWh on 27 Sep.
//   - house use for a finished day: Amber grid + Sigen solar and battery, so
//     the Sankey balances on meter figures. Sigen's own grid reading runs
//     ~6% low (more when Amber charges the battery from the grid).
//
// Amber's price descriptor ("very_low" etc.) is not used: on 2026-10-22 it
// said very_low for every slot from 21c to 33c, so it cannot rank hours.

export const AMBER = {
  price: 'sensor.2_wallace_street_balmain_general_price',
  feedIn: 'sensor.2_wallace_street_balmain_feed_in_price',
  forecast: 'sensor.2_wallace_street_balmain_general_forecast',
  predicted: 'sensor.amber_general_predicted_forecast',
  spike: 'binary_sensor.2_wallace_street_balmain_price_spike',
  renewables: 'sensor.2_wallace_street_balmain_renewables',
};
export const SIGEN = {
  solar: 'sensor.home_battery_system_pv_power',             // kW
  house: 'sensor.home_battery_system_consumed_power',       // kW
  grid: 'sensor.home_battery_system_grid_active_power',     // kW, + buying
  battery: 'sensor.home_battery_system_battery_power',      // kW, + charging
  soc: 'sensor.home_battery_system_battery_state_of_charge',
  capacity: 'sensor.home_battery_system_rated_energy_capacity',
};
export const TODAY = {
  cost: 'sensor.amber_grid_import_cost_daily',     // HA utility meters,
  earned: 'sensor.amber_grid_export_revenue_daily', // reset at midnight
  bought: 'sensor.home_battery_system_daily_grid_import_energy',
  sold: 'sensor.home_battery_system_daily_grid_export_energy',
};
export const SOLCAST = {
  today: 'sensor.solcast_pv_forecast_forecast_today',       // kWh
  peakAt: 'sensor.solcast_pv_forecast_peak_time_today',
  peak: 'sensor.solcast_pv_forecast_peak_forecast_today',   // W
};
// Long-term statistics read for past days, by role.
export const STATS = {
  cost: 'amber:import_cost',
  earned: 'amber:export_earnings',
  bought: 'amber:grid_import',
  sold: 'amber:grid_export',
  solar: 'sensor.home_battery_system_sigen_pv_energy_derived',
  batteryIn: 'sensor.home_battery_system_total_charged_energy_of_the_ess',
  batteryOut: 'sensor.home_battery_system_total_discharged_energy_of_the_ess',
};

// Amber's daily supply charge plus membership, $642 a year
// (ha-battery docs/retailer-survey.md §10). Not in any usage figure.
export const FIXED_PER_DAY = 642 / 365;

// A price within this many cents of the cheapest stretch counts as cheap.
export const NEAR_C = 2;
// Waiting is only worth suggesting when it saves at least this much.
export const WORTH_WAITING_C = 5;
// Length of the cheapest and dearest stretches, in half-hour slots.
export const WINDOW_SLOTS = 6;

const BLANK = new Set(['unknown', 'unavailable', 'none', '']);

function raw(states, id) {
  const e = states?.[id];
  if (!e || e.state == null || BLANK.has(String(e.state).toLowerCase())) return null;
  return e.state;
}
function num(states, id) {
  const v = raw(states, id);
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function time(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}
const toCents = (perKwh) => (perKwh == null ? null : perKwh * 100);

// ── Price now and ahead ────────────────────────────────────────────

export function readPrice(states) {
  const now = num(states, AMBER.price);
  if (now == null) return null;
  const a = states[AMBER.price].attributes || {};
  return {
    cents: toCents(now),
    feedInCents: toCents(num(states, AMBER.feedIn)),
    renewables: num(states, AMBER.renewables),
    spike: raw(states, AMBER.spike) === 'on' || a.spike_status === 'spike',
    start: time(a.start_time),
    end: time(a.end_time),
  };
}

// Half-hour slots from the current one onwards. The current one comes from
// the price sensor itself, as that is what is being charged now. The rest
// are Amber's prediction (cents, with low and high), or the integration's
// per_kwh forecast (no range) when the prediction sensor has nothing ahead.
export function readSlots(states, now) {
  const price = readPrice(states);
  const slots = [];
  if (price?.start && price?.end && price.end > now) {
    slots.push({ start: price.start, end: price.end, cents: price.cents, spike: price.spike });
  }
  const take = (list, pick) => {
    const out = [];
    if (!Array.isArray(list)) return out;
    for (const f of list) {
      const start = time(f.start_time), end = time(f.end_time), v = pick(f);
      if (!start || !end || end <= now || !v) continue;
      if ((out.length ? out[out.length - 1].end : slots[0]?.end) > start) continue;
      out.push({ start, end, ...v, spike: f.spike_status === 'spike' });
    }
    return out;
  };
  const num3 = (f, k) => (typeof f[k] === 'number' ? f[k] * 100 : null);
  const predicted = take(states?.[AMBER.predicted]?.attributes?.forecasts, (f) => {
    const cents = num3(f, 'predicted');
    return cents == null ? null : { cents, low: num3(f, 'low'), high: num3(f, 'high') };
  });
  const ahead = predicted.length ? predicted
    : take(states?.[AMBER.forecast]?.attributes?.forecasts, (f) => (typeof f.per_kwh === 'number' ? { cents: f.per_kwh * 100 } : null));
  return slots.concat(ahead);
}

function windowOf(slots, n, pick) {
  const k = Math.min(n, slots.length);
  let best = null;
  for (let i = 0; i + k <= slots.length; i++) {
    const avg = slots.slice(i, i + k).reduce((s, x) => s + x.cents, 0) / k;
    if (!best || pick(avg, best.avg)) best = { start: slots[i].start, end: slots[i + k - 1].end, avg };
  }
  return best;
}

// The verdict the card and page lead with. `kind` is one of spike, cheap,
// dear, later, flat.
export function advise(price, slots) {
  if (!price || slots.length < 2) return null;
  const cents = slots.map((s) => s.cents);
  const min = Math.min(...cents), max = Math.max(...cents);
  // A window on tomorrow's date says so: at 3 pm a bare "7:30 to 10:30 pm"
  // reads as tonight. The small hours of the coming night stay bare ("2 to
  // 5 am" at 3 pm), unless it is already the small hours, when the next
  // ones are a day away.
  const t0 = slots[0].start, day0 = localDate(t0);
  const when = (w) => w && localDate(w.start) > day0
    && !(w.start.getHours() < 5 && t0.getHours() >= 5) ? { ...w, tomorrow: true } : w;
  const cheap = when(windowOf(slots, WINDOW_SLOTS, (a, b) => a < b));
  const dear = when(windowOf(slots, WINDOW_SLOTS, (a, b) => a > b));
  const until = slots[slots.length - 1].end;
  const base = { min, max, cheap, dear, until };
  const range = `Between ${Math.round(min)}c and ${Math.round(max)}c until ${fmtClock(until)}.`;

  if (price.spike) {
    let spikeEnd = null;
    for (const s of slots) { if (!s.spike) break; spikeEnd = s.end; }
    // The washer and dryer are held off by HA during a spike (power/laundry.js),
    // so the advice names only what nobody switches for you.
    return {
      ...base, kind: 'spike', headline: 'Price spike', spikeEnd,
      detail: `Hold off on the dishwasher${spikeEnd ? ` until about ${fmtClock(spikeEnd)}` : ''}.`,
    };
  }
  const p = price.cents;
  if (p <= cheap.avg + NEAR_C) {
    return { ...base, kind: 'cheap', headline: 'About as cheap as it gets', detail: `${range} Dearest ${fmtRange(dear)}.` };
  }
  if (p >= dear.avg - NEAR_C) {
    return { ...base, kind: 'dear', headline: 'One of the dearest times', detail: `Cheaper ${fmtRange(cheap)}, about ${Math.round(cheap.avg)}c.` };
  }
  if (p - cheap.avg >= WORTH_WAITING_C) {
    return { ...base, kind: 'later', headline: 'Cheaper later', detail: `${cap(fmtRange(cheap))}, about ${Math.round(cheap.avg)}c.` };
  }
  return { ...base, kind: 'flat', headline: 'Prices are fairly flat', detail: `${range} Dearest ${fmtRange(dear)}.` };
}

// ── Right now ──────────────────────────────────────────────────────

export function readNow(states) {
  const soc = num(states, SIGEN.soc);
  const batteryKw = num(states, SIGEN.battery);
  return {
    solarKw: num(states, SIGEN.solar),
    houseKw: num(states, SIGEN.house),
    gridKw: num(states, SIGEN.grid),
    batteryKw,
    soc,
    capacityKwh: num(states, SIGEN.capacity),
    battery: batteryWords(soc, batteryKw),
    solarToday: num(states, SOLCAST.today),
    solarPeakAt: time(raw(states, SOLCAST.peakAt)),
    solarPeakKw: num(states, SOLCAST.peak) != null ? num(states, SOLCAST.peak) / 1000 : null,
  };
}

// "Empty", "62%", plus what it is doing.
export function batteryWords(soc, kw) {
  if (soc == null) return null;
  const doing = kw == null ? null : kw > 0.05 ? 'charging' : kw < -0.05 ? 'in use' : null;
  const level = soc <= 0.5 ? 'Empty' : soc >= 99.5 ? 'Full' : `${Math.round(soc)}%`;
  return { level, doing, short: doing ? `${level}, ${doing}` : level };
}

// ── Today so far (HA's estimate) ───────────────────────────────────

export function readToday(states) {
  const cost = num(states, TODAY.cost);
  if (cost == null) return null;
  const earned = num(states, TODAY.earned) ?? 0;
  return {
    cost, earned, fixed: FIXED_PER_DAY,
    net: cost - earned + FIXED_PER_DAY,
    boughtKwh: num(states, TODAY.bought),
    soldKwh: num(states, TODAY.sold),
  };
}

// ── Past days (Amber settled) ──────────────────────────────────────

// Local calendar date, YYYY-MM-DD.
export function localDate(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// `result` is a recorder/statistics_during_period answer for STATS with
// period "day" and type "change". A day counts only once Amber has settled
// it, i.e. it has an amber:import_cost row, and never from `today` on:
// Amber's settlement day runs on NEM time (no daylight saving), so after
// a night's import today already holds one settled hour.
export function dailyFromStats(result, today = null) {
  const byDay = new Map();
  for (const [role, id] of Object.entries(STATS)) {
    for (const row of result?.[id] || []) {
      if (typeof row.change !== 'number') continue;
      const date = localDate(new Date(row.start));
      if (!byDay.has(date)) byDay.set(date, { date });
      byDay.get(date)[role] = row.change;
    }
  }
  return [...byDay.values()]
    .filter((d) => typeof d.cost === 'number' && (!today || d.date < today))
    .sort((a, b) => (a.date < b.date ? -1 : 1))
    .map((d) => ({
      ...d,
      earned: d.earned ?? 0,
      bought: d.bought ?? 0,
      sold: d.sold ?? 0,
      net: d.cost - (d.earned ?? 0) + FIXED_PER_DAY,
    }));
}

export function totals(days) {
  const sum = (k) => days.reduce((s, d) => s + (d[k] ?? 0), 0);
  const t = {
    days: days.length,
    cost: sum('cost'), earned: sum('earned'), fixed: FIXED_PER_DAY * days.length,
    bought: sum('bought'), sold: sum('sold'),
    solar: sum('solar'), batteryIn: sum('batteryIn'), batteryOut: sum('batteryOut'),
  };
  t.net = t.cost - t.earned + t.fixed;
  t.house = t.bought + t.solar + t.batteryOut - t.sold - t.batteryIn;
  return t;
}

// The day that most changes the month's story: a sale big enough to turn
// the day negative.
export function standoutDay(days) {
  const neg = days.filter((d) => d.net < 0).sort((a, b) => a.net - b.net);
  return neg[0] || null;
}

// "A normal day": the median, so one spike sale does not move it.
export function typicalDay(days) {
  if (!days.length) return null;
  const v = days.map((d) => d.net).sort((a, b) => a - b);
  const m = Math.floor(v.length / 2);
  return v.length % 2 ? v[m] : (v[m - 1] + v[m]) / 2;
}

// Sources and uses for the Sankey, through one central node so nothing is
// guessed about which source fed which use.
export function sankeyFlows(t) {
  return {
    sources: [
      { key: 'solar', label: 'Solar', kwh: t.solar },
      { key: 'grid', label: 'Grid', kwh: t.bought, dollars: t.cost },
      { key: 'battery', label: 'Battery', kwh: t.batteryOut },
    ],
    uses: [
      { key: 'house', label: 'House', kwh: Math.max(0, t.house) },
      { key: 'battery', label: 'Battery', kwh: t.batteryIn },
      { key: 'sold', label: 'Sold', kwh: t.sold, dollars: t.earned },
    ],
  };
}

// ── Formatting ─────────────────────────────────────────────────────

// "2 am", "7:30 pm", "noon", "midnight".
export function fmtClock(d) {
  if (!d) return '';
  const h = d.getHours(), m = d.getMinutes();
  if (m === 0 && h === 12) return 'noon';
  if (m === 0 && h === 0) return 'midnight';
  const h12 = h % 12 || 12;
  return `${h12}${m ? `:${String(m).padStart(2, '0')}` : ''} ${h < 12 ? 'am' : 'pm'}`;
}
// "2 to 5 am", "7:30 to 10:30 pm", "11 pm to 2 am", "tomorrow 1 to 4 pm".
export function fmtRange(w) {
  if (!w) return '';
  const a = fmtClock(w.start), b = fmtClock(w.end);
  const sa = a.split(' '), sb = b.split(' ');
  const r = sa.length === 2 && sb.length === 2 && sa[1] === sb[1] ? `${sa[0]} to ${b}` : `${a} to ${b}`;
  return w.tomorrow ? `tomorrow ${r}` : r;
}
export const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Centre x for each window label above the price chart. Labels sit on one
// line, so two windows a few hours apart ran their text together (2026-10-26:
// "Cheapest 1 to 4 pm" over "Dearest 7:30 to 10:30 pm"). Full labels if they
// fit; otherwise the short words, pushed apart if even those would touch. The
// times are on the Price card either way. Every label stays inside [0, width].
// items: [{ a, b, full, short }] in pixels; measure(text) -> pixel width.
export function placeBrackets(items, width, measure, gap = 12) {
  const lay = (key) => items.map((it) => {
    const text = it[key], w = measure(text);
    const half = Math.min(w / 2, width / 2);
    return { text, w, x: Math.max(half, Math.min((it.a + it.b) / 2, width - half)) };
  });
  const clash = (ls) => ls.some((p, i) => ls.slice(i + 1).some((q) => Math.abs(p.x - q.x) < (p.w + q.w) / 2 + gap));
  const full = lay('full');
  if (!clash(full)) return full;
  const out = lay('short');
  if (out.length !== 2 || !clash(out)) return out;
  const [l, r] = out[0].x <= out[1].x ? out : [out[1], out[0]];
  const need = (l.w + r.w) / 2 + gap - (r.x - l.x);
  l.x -= need / 2; r.x += need / 2;
  if (l.x < l.w / 2) { r.x += l.w / 2 - l.x; l.x = l.w / 2; }
  if (r.x > width - r.w / 2) { l.x -= r.x - (width - r.w / 2); r.x = width - r.w / 2; }
  return out;
}

// "$6.26", "$231", "-$159.82" shown as "+$159.82 earned" by callers.
export function fmtMoney(v, { whole = false } = {}) {
  if (v == null) return '–';
  const a = Math.abs(v);
  const s = whole || a >= 100 ? Math.round(a).toLocaleString('en-AU') : a.toFixed(2);
  return `${v < 0 ? '-' : ''}$${s}`;
}
export function fmtKwh(v) {
  if (v == null) return '–';
  return `${v >= 100 ? Math.round(v).toLocaleString('en-AU') : v.toFixed(1)} kWh`;
}
export function fmtDayShort(dateStr) {
  const d = new Date(`${dateStr}T12:00:00`);
  return `${d.getDate()} ${d.toLocaleDateString('en-AU', { month: 'short' })}`;
}
export function fmtDayLong(dateStr) {
  const d = new Date(`${dateStr}T12:00:00`);
  return d.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' });
}
